<template>
  <o-modal v-if="isVisible" @close="handleClose">
    <template v-slot:header>
        <h5 class="modal-title">Gift Voucher: <strong>{{ voucher?.name }}</strong></h5>
    </template>

    <template v-slot:body>
        <!-- Target Selector Component -->
        <GiftVoucherTargetSelector
          :tiers="tiers"
          :corporateId="corporateId"
          :allMemberCount="memberPreview.total_member"
          v-model:targetType="form.target_type"
          v-model:tierIds="form.tier_ids"
          v-model:isAllTiers="form.is_all_tiers"
          v-model:memberIds="form.member_ids"
          @change="onTargetChange"
        />

        <!-- Preview -->
        <div class="preview-box mt-3" v-if="previewLoaded">
          <span class="preview-label">To be sent to:</span>
          <span class="preview-count">{{ memberPreview.total_member }} members</span>
        </div>

        <!-- Note -->
        <div class="form-group mt-3">
          <label>Note (Optional)</label>
          <textarea
            v-model="form.note"
            class="form-control"
            rows="2"
            maxlength="500"
            placeholder="Example: Special shop opening voucher"
          ></textarea>
        </div>

        <!-- Error -->
        <div v-if="errorMessage" class="alert alert-danger mt-3 py-2">{{ errorMessage }}</div>

        <div class="d-flex justify-content-end gap-2 mt-4">
            <o-button type="button" class="btn-custom btn-custom-outline" @click="handleClose" :disabled="isLoading">Cancel</o-button>
            <o-button
            type="button"
            class="btn-custom btn-custom-primary"
            @click="submitGift"
            :disabled="isLoading || isSubmitDisabled"
            >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
            <template v-if="isLoading">Memproses...</template>
            <template v-else-if="previewLoading">
                <span class="spinner-border spinner-border-sm me-1"></span> Memuat preview...
            </template>
            <template v-else-if="previewLoaded">
                Send to {{ memberPreview.total_member }} members
            </template>
            <template v-else>Send Voucher</template>
            </o-button>
        </div>
    </template>
  </o-modal>
</template>

<script>
import { reactive, ref, watch, computed } from 'vue'
import store from '@/store/'
import { OModal, OButton } from '@/components/OmnihControl'
import GiftVoucherTargetSelector from './GiftVoucherTargetSelector.vue'
import { useModalHistory } from '@/composables/useModalHistory'

export default {
  name: 'GiftVoucherModal',
  components: { OModal, OButton, GiftVoucherTargetSelector },
  props: {
    isVisible: { type: Boolean, default: false },
    voucher: { type: Object, default: null },
  },
  emits: ['close', 'gift-success'],

  setup(props, { emit }) {
    const tiers = ref([])
    const isLoading = ref(false)
    const previewLoaded = ref(false)
    const previewLoading = ref(false)
    const errorMessage = ref('')
    const debounceTimer = ref(null)
    const corporateId = ref(null)

    // Computed: disable submit based on target_type
    const isSubmitDisabled = computed(() => {
      if (previewLoading.value) return true
      if (form.target_type === 'tier') {
        return !form.is_all_tiers && form.tier_ids.length === 0
      }
      if (form.target_type === 'selected') return form.member_ids.length === 0
      // 'all' — enable once preview has been attempted (success or fail)
      return false
    })

    const memberPreview = computed(() => store.state.VoucherGift.memberPreview)

    const form = reactive({
      target_type: 'tier',
      tier_ids: [],
      is_all_tiers: true,
      member_ids: [],
      note: '',
    })

    const { pushModalHistory, popModalHistory } = useModalHistory({
      isVisible: computed(() => props.isVisible),
      onClose: () => {
        emit('close')
      },
      stateKey: 'gift-voucher-modal'
    })

    // Load business profile to get corporate_id
    async function loadBusinessProfile() {
      try {
        const businessProfile = await store.dispatch('Auth/getBusinessProfile')
        corporateId.value = businessProfile.corporate_id
      } catch (e) {
        console.error('Failed to load business profile', e)
      }
    }

    // Load tiers on mount
    async function loadTiers() {
      try {
        // Use Newsletter/readTiers because it includes member counts per tier
        await store.dispatch('Newsletter/readTiers')
        tiers.value = store.state.Newsletter.tiers || []
      } catch (e) {
        console.error('Failed to load tiers', e)
      }
    }

    // Trigger preview when modal opens or target changes
    watch(() => props.isVisible, (newVal) => {
      if (newVal) {
        resetForm()
        loadBusinessProfile()
        loadTiers()
        triggerPreview()
        pushModalHistory()
      }
    })

    function resetForm() {
      form.target_type = 'tier'
      form.tier_ids = []
      form.is_all_tiers = true
      form.member_ids = []
      form.note = ''
      previewLoaded.value = false
      previewLoading.value = false
      errorMessage.value = ''
    }

    function onTargetChange() {
      clearTimeout(debounceTimer.value)
      debounceTimer.value = setTimeout(() => triggerPreview(), 500)
    }

    async function triggerPreview() {
      if (!props.voucher) return
      // Skip preview for 'selected' with no members, or 'tier' with no tier
      if (form.target_type === 'tier' && !form.is_all_tiers && form.tier_ids.length === 0) return
      if (form.target_type === 'selected' && form.member_ids.length === 0) return

      const mappedTargetType = (form.target_type === 'tier' && form.is_all_tiers) ? 'all' : form.target_type;

      const params = { 
        target_type: mappedTargetType,
        is_all_tiers: form.is_all_tiers ? 1 : 0
      }
      if (form.target_type === 'tier' && !form.is_all_tiers) params['tier_ids[]'] = form.tier_ids
      if (form.target_type === 'selected') params['member_ids[]'] = form.member_ids

      previewLoading.value = true
      previewLoaded.value = false
      try {
        await store.dispatch('VoucherGift/previewTarget', params)
        previewLoaded.value = true
      } catch (e) {
        console.error('Preview failed', e)
        // Don't block the button on preview failure
        previewLoaded.value = false
      } finally {
        previewLoading.value = false
      }
    }

    async function submitGift() {
      if (!props.voucher) return
      isLoading.value = true
      errorMessage.value = ''

      const mappedTargetType = (form.target_type === 'tier' && form.is_all_tiers) ? 'all' : form.target_type;

      const data = {
        voucher_id: props.voucher.id,
        target_type: mappedTargetType,
        is_all_tiers: form.is_all_tiers ? 1 : 0,
        note: form.note || undefined,
      }
      if (form.target_type === 'tier' && !form.is_all_tiers) data.tier_ids = form.tier_ids
      if (form.target_type === 'selected') data.member_ids = form.member_ids

      try {
        const result = await store.dispatch('VoucherGift/giftVoucher', { data })
        emit('gift-success', result.data)
        popModalHistory()
      } catch (err) {
        errorMessage.value = err?.response?.data?.message || 'An error occurred. Please try again.'
      } finally {
        isLoading.value = false
      }
    }

    const handleClose = () => {
      emit('close')
      popModalHistory()
    }

    return { 
      tiers, isLoading, previewLoaded, previewLoading, isSubmitDisabled, 
      errorMessage, form, memberPreview, corporateId, onTargetChange, 
      submitGift, handleClose 
    }
  }
}
</script>

<style scoped>
.preview-box {
  background: color-mix(in srgb, var(--primary-color), white 92%);
  border: 1px solid color-mix(in srgb, var(--primary-color), white 85%);
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.preview-label { color: #64748b; font-size: 0.85rem; font-weight: 500; }
.preview-count { font-weight: 700; color: var(--primary-color); font-size: 1.1rem; }

/* Custom Buttons Styling from TransactionForm pattern */
.btn-custom {
    font-weight: 600;
    font-size: 12px;
    min-height: 36px;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    padding: 6px 16px;
    transition: all 0.2s;
    border: none;
    cursor: pointer;
    box-shadow: none !important;
}

.btn-custom-primary {
    background-color: var(--primary-color) !important;
    color: white !important;
}

.btn-custom-primary:hover {
    filter: brightness(0.9);
}

.btn-custom-outline {
    background: #ffffff !important;
    color: var(--primary-color, #F93E3E) !important;
    border: 1px solid var(--primary-border, #F93E3E) !important;
}

.btn-custom-outline:hover {
    background: var(--secondary-color, #F93E3E) !important;
    color: var(--primary-color, #F93E3E) !important;
}

.form-control:focus {
    border-color: var(--primary-color, #F93E3E) !important;
    box-shadow: 0 0 0 0.25rem color-mix(in srgb, var(--primary-color), transparent 75%) !important;
}
</style>
